/**
 * PortFerryCard — v3.7.1.
 *
 * Dedicated conditional card that renders the port arrival details for a
 * booking. Fires purely on the pickup location NAME (case-insensitive
 * "port") — independent of the location_type field so it works even when
 * the admin hasn't tagged a location as `port` in Rental Settings.
 *
 * Data model: bound to a nested `ferry_details: { company, vessel_name,
 * arrival_time }` on the parent booking form.
 *
 * Styling mirrors the airport / flight-details block emitted by
 * `CheckoutFields.jsx` (white card, uppercase field labels, 2-col grid).
 */
export default function PortFerryCard({
  pickupLocation,
  value,
  onChange,
  testIdPrefix = "pf",
}) {
  const isPort = (pickupLocation || "").toLowerCase() === "port";
  if (!isPort) return null;

  const v = value || { company: "", vessel_name: "", arrival_time: "" };
  const set = (patch) => onChange({ ...v, ...patch });

  return (
    <div
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-5"
      data-testid={`${testIdPrefix}-port-ferry-card`}
    >
      <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-3">
        Port pickup — ferry details
      </p>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Ferry company / operator">
          <input
            type="text"
            value={v.company}
            onChange={(e) => set({ company: e.target.value })}
            placeholder="e.g. SeaJets, Blue Star Ferries"
            className="editorial-input w-full"
            data-testid={`${testIdPrefix}-ferry-company`}
          />
        </Field>
        <Field label="Vessel / ship name">
          <input
            type="text"
            value={v.vessel_name}
            onChange={(e) => set({ vessel_name: e.target.value })}
            placeholder="e.g. WorldChampion Jet, Highspeed 4"
            className="editorial-input w-full"
            data-testid={`${testIdPrefix}-ferry-vessel`}
          />
        </Field>
        <Field label="Arrival time">
          <input
            type="time"
            value={v.arrival_time}
            onChange={(e) => set({ arrival_time: e.target.value })}
            className="editorial-input w-full"
            data-testid={`${testIdPrefix}-ferry-arrival-time`}
          />
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="col-span-1">
      <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
